import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Wind, Brain, Activity, Unplug } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const ICONS = [Wind, Activity, Brain, Unplug];
const COLORS = ["#FF2D5C", "#FF3DCB", "#9B4DFF", "#3DBFFF"];

const Metoden = () => {
  const { t } = useTranslation();
  const cards = t("metoden.cards", { returnObjects: true });

  return (
    <section
      id="metoden"
      data-testid="metoden-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1280px] px-5 md:px-10">
        {/* Header block — eyebrow, h2, lead.  Wider container ensures the
            headline reads as a single line on desktop. */}
        <div className="max-w-4xl">
          <span className="eyebrow">{t("metoden.eyebrow")}</span>
          <h2 className="font-display mt-5 text-lg sm:text-xl lg:text-[1.6rem] font-semibold leading-[1.18] tracking-[-0.005em] text-white glow-text-white uppercase">
            {t("metoden.title")}
          </h2>
          <p className="mt-7 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
            {t("metoden.lead")}
          </p>
        </div>

        {/* "Fundamentet i metoden" — section label */}
        <div className="mt-14 flex items-center gap-3 text-xs font-mono uppercase tracking-[0.22em] text-[#6FF6FF]">
          <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#6FF6FF]" />
          {t("metoden.pillars_label")}
        </div>

        {/* Four pillar cards — side by side on lg, 2-up on tablet, 1-up on mobile */}
        <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.isArray(cards) &&
            cards.map((c, i) => {
              const Icon = ICONS[i % ICONS.length];
              const color = COLORS[i % COLORS.length];
              return (
                <motion.div
                  key={i}
                  data-testid={`metoden-card-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="glass relative overflow-hidden rounded-2xl p-7 electric-border"
                >
                  {/* corner glow */}
                  <div
                    className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-50 blur-3xl"
                    style={{ background: color }}
                  />
                  <div
                    className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10"
                    style={{
                      background: `linear-gradient(135deg, ${color}25, transparent)`,
                      boxShadow: `0 0 24px -6px ${color}80`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color }} />
                  </div>
                  <h3 className="font-display relative mt-5 text-lg md:text-xl font-semibold leading-snug text-white">
                    {c.title}
                  </h3>
                  <p className="relative mt-3 text-sm leading-relaxed text-white/65">
                    {c.body}
                  </p>
                  <span
                    className="absolute right-5 top-5 font-mono text-[11px] uppercase tracking-widest"
                    style={{ color }}
                  >
                    0{i + 1}
                  </span>
                </motion.div>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default Metoden;
